/**
 * Tabs is a class that manages the behavior of a tabbed interface.
 * It takes three arguments: a selector for the tab list, a selector for the thumbnail container, and a selector for the content container.
 * 
 * The constructor queries the DOM for these elements and stores references to them.
 * It also queries for individual tabs, thumbnails, and content items within these containers.
 * 
 * The init method is called to set up the initial state of the interface and add event listeners.
 * 
 * The toggleTabs method is called when a tab is clicked. It updates the active state of the tabs and thumbnails.
 * 
 * The toggleContent method is called when a thumbnail is clicked or when a tab is clicked for the first time. It updates the active state of the content items.
 */

class Tabs {
  constructor(tabList, thumbnailContainer, contentContainer) {
    this.tabList = document.querySelector(tabList);
    this.thumbnailContainer = document.querySelector(thumbnailContainer);
    this.contentContainer = document.querySelector(contentContainer);

    if (!this.tabList || !this.thumbnailContainer || !this.contentContainer)
      return;

    this.tabs = Array.from(
      this.tabList.querySelectorAll(".product-tabs__tab-link")
    );
    this.thumbnails = Array.from(
      this.thumbnailContainer.querySelectorAll(".product-tabs__thumbnail")
    );
    this.contents = Array.from(
      this.contentContainer.querySelectorAll(".product-tabs__content-item")
    );

    this.init();
  }

  init() {
    // Add active class to all thumbnails, and content by default
    this.thumbnails.forEach((thumbnail) => {
      thumbnail.classList.add("active");
    });
    if (this.contents[0]) this.contents[0].classList.add("active");
    if (this.thumbnails[0])
      this.thumbnails[0].classList.add("product-content-open");

    this.tabs.forEach((tab) => {
      // Handle tab click event
      tab.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleTabs(e.target);
      });
    });

    this.thumbnails.forEach((thumbnail) => {
      // Handle thumbnail click event
      thumbnail.addEventListener("click", (e) => {
        this.toggleContent(e.currentTarget.getAttribute("data-block-id"));
      });
    });
  }

  toggleTabs(activeTab) {
    const tabTitle = activeTab.getAttribute("data-tab-title");

    // Remove active class from all tabs and thumbnails
    this.tabs.forEach((tab) => tab.classList.remove("active"));
    this.thumbnails.forEach((thumbnail) =>
      thumbnail.classList.remove("active")
    );

    // Add active class to clicked tab
    activeTab.classList.add("active");

    // Add active class to matching thumbnails
    this.thumbnails.forEach((thumbnail) => {
      let matchingThumbnailsCount = 0;
      if (thumbnail.getAttribute("data-tab-title") === tabTitle) {
        matchingThumbnailsCount++;
        thumbnail.classList.add("active");
        if (matchingThumbnailsCount == 1) {
          const thumbnailBlockId = thumbnail.getAttribute("data-block-id");
          this.toggleContent(thumbnailBlockId);
        }
      }
    });
  }

  // Toggle Content based on block ID
  toggleContent(blockId) {
    // Remove active class from all content items and "product-content-open" from thumbnails
    this.contents.forEach((content) => {
      content.classList.remove("active");
    });
    this.thumbnails.forEach((thumbnail) => {
      thumbnail.classList.remove("product-content-open");
    });

    // Add active class to matching content item
    this.contents.forEach((content) => {
      if (content.getAttribute("data-block-id") === blockId) {
        content.classList.add("active");
        const openThumbnail = this.thumbnailContainer.querySelector(
          `[data-block-id="${blockId}"]`
        );
        openThumbnail.classList.add("product-content-open");
      }
    });
  }
}

document.addEventListener("DOMContentLoaded", () => {
  new Tabs(
    ".product-tabs__tab-list",
    ".product-tabs__thumbnails",
    ".product-tabs__content"
  );
});
